import * as types from '../../../types';
import {appliedDiscountFactory, percentOf, sumTaxLines} from '../../../../utils';
import {RepositoryFactory} from '../../../../repositories/RepositoryFactory';
import Decimal from 'decimal.js';
import _ from 'lodash';

const ShopifyProductRepository = RepositoryFactory.get('acadia/shopify/products/product');

export default {
  state: {
    loading: true, items: [], search: '',
  }, mutations: {
    /**
     * Adds a product to the state.
     * @param {Object} state - The current state.
     * @param {Object} product - The product to add.
     */
    [types.ORDER_SET_PRODUCTS_ADD](state, product) {
      state.items.push(product);
    }, /**
     * Initializes the state with a list of products.
     * @param {Object} state - The current state.
     * @param {Array} products - The list of products to initialize.
     */
    [types.ORDER_SET_PRODUCTS_INIT](state, products = []) {
      state.items = products;
    }, /**
     * Sets the loading status of the state.
     * @param {Object} state - The current state.
     * @param {boolean} status - The loading status.
     */
    [types.ORDER_SET_PRODUCTS_LOADING](state, status) {
      state.loading = status;
    }, /**
     * Sets the quantity of a product in the state.
     * @param {Object} state - The current state.
     * @param {Object} payload - The payload containing productId and quantity.
     */
    [types.ORDER_SET_PRODUCTS_QUANTITY](state, {productId, quantity}) {
      state.items[productId].quantity = Number(quantity) ? Number(quantity) : 0;
    }, /**
     * Removes a product from the state by its key.
     * @param {Object} state - The current state.
     * @param {number} key - The key of the product to remove.
     */
    [types.ORDER_SET_PRODUCTS_REMOVE](state, key) {
      state.items = state.items.filter((item, index) => {
        return index !== key;
      });
    }, /**
     * Sets the search term in the state.
     * @param {Object} state - The current state.
     * @param {string} search - The search term.
     */
    [types.ORDER_SET_PRODUCTS_SEARCH](state, search = '') {
      state.search = search;
    }, /**
     * Sets the total price of a product in the state.
     * @param {Object} state - The current state.
     * @param {Object} payload - The payload containing productId and total.
     */
    [types.ORDER_SET_PRODUCTS_TOTAL](state, {productId, total}) {
      state.items[productId].total = Number(total) > 0 ? Number(total) : 0;
    }, /**
     * Sets the variant of a product in the state.
     * @param {Object} state - The current state.
     * @param {Object} payload - The payload containing productId and variant.
     */
    [types.ORDER_SET_PRODUCTS_VARIANT](state, {productId, variant}) {
      state.items[productId].variant = variant;
    },
  }, actions: {
    /**
     * Adds a product to the order.
     * @param {Object} context - The Vuex context.
     * @param {Object} lineItem - The line item to add.
     */
    async [types.ORDER_PRODUCTS_ADD]({commit, dispatch, rootState, rootGetters}, lineItem) {
      const {order} = rootState;
      const {region} = order;
      const response = await ShopifyProductRepository.getProduct(region, lineItem.product_id);
      const product = response.data.product;
      const variant = product.variants.find(variant => variant.id === lineItem.id || variant.sku === lineItem.sku);
      const subTotal = (new Decimal(lineItem.price)).times(lineItem.quantity);
      const price = subTotal.div(lineItem.quantity);
      const discountPercent = rootGetters[types.ORDER_GET_DISCOUNT_PERCENT];
      const discountTotal = percentOf(lineItem.quantity * price, discountPercent);

      product['line_item'] = lineItem;
      product['variant'] = variant;
      product['variant']['new_price'] = price.toDecimalPlaces(2).toNumber();
      product['quantity'] = lineItem.quantity;
      product['price'] = price.toDecimalPlaces(2).toNumber();
      product['total'] = subTotal.plus(discountTotal).toDecimalPlaces(2).toNumber();

      commit(types.ORDER_SET_PRODUCTS_ADD, product);
      dispatch(types.ORDER_PRODUCTS_UPDATE_TOTALS);
    }, /**
     * Initializes the products in the order.
     * @param {Object} context - The Vuex context.
     * @param {Object} order - The order to initialize products for.
     */
    [types.ORDER_PRODUCTS_INIT]({commit, dispatch}, order) {
      commit(types.ORDER_SET_PRODUCTS_LOADING, true);
      commit(types.ORDER_SET_PRODUCTS_INIT);

      order.line_items.forEach(lineItem => {
        dispatch(types.ORDER_PRODUCTS_ADD, lineItem);
      });
    }, /**
     * Removes a product from the order.
     * @param {Object} context - The Vuex context.
     * @param {number} key - The key of the product to remove.
     */
    [types.ORDER_PRODUCTS_REMOVE]({commit, dispatch}, key) {
      commit(types.ORDER_SET_PRODUCTS_REMOVE, key);
      dispatch(types.ORDER_PRODUCTS_UPDATE_TOTALS);
    }, /**
     * Searches for a product by its handle.
     * @param {Object} context - The Vuex context.
     */
    [types.ORDER_PRODUCTS_SEARCH]({state, commit, rootState}) {
      const {order} = rootState;
      const {region} = order;
      const {search} = state;

      if (search.trim() === '') {
        return;
      }

      ShopifyProductRepository.searchProductHandle(region, search).then(response => {
        return response.data.products.pop();
      }).then(product => {
        for (let i = 0; i < product.variants.length; i++) {
          const variant = product.variants[i];
          const price = Number(variant.price);
          product['price'] = price;

          if (variant.inventory_quantity > 0) {
            const quantity = 1;
            const total = quantity * price;

            product['variant'] = variant;
            product['variant']['new_price'] = price;
            product['quantity'] = quantity;
            product['total'] = total;
            break;
          } else {
            product['variant'] = {
              id: null, price: 0, new_price: 0,
            };
            product['quantity'] = 0;
            product['total'] = 0;
          }
        }

        commit(types.ORDER_SET_PRODUCTS_ADD, product);
      });
    }, /**
     * Updates the price of a product in the order.
     * @param {Object} context - The Vuex context.
     * @param {Object} payload - The payload containing productId and price.
     */
    [types.ORDER_PRODUCTS_UPDATE_PRICE]({state, commit, dispatch}, {productId, price}) {
      const product = _.cloneDeep(state.items[productId]);
      const variant = _.cloneDeep(product.variant);
      variant['new_price'] = Number(price) ? Number(price) : 0;
      commit(types.ORDER_SET_PRODUCTS_VARIANT, {productId, variant});
      dispatch(types.ORDER_PRODUCTS_UPDATE_TOTALS);
    }, /**
     * Updates the quantity of a product in the order.
     * @param {Object} context - The Vuex context.
     * @param {Object} payload - The payload containing productId and quantity.
     */
    [types.ORDER_PRODUCTS_UPDATE_QUANTITY]({commit, dispatch}, {productId, quantity}) {
      commit(types.ORDER_SET_PRODUCTS_QUANTITY, {productId, quantity});
      dispatch(types.ORDER_PRODUCTS_UPDATE_TOTALS);
    }, /**
     * Updates the total prices of all products in the order.
     * @param {Object} context - The Vuex context.
     */
    [types.ORDER_PRODUCTS_UPDATE_TOTALS]({state, commit, rootGetters}) {
      const products = [...state.items];
      const discountPercent = rootGetters[types.ORDER_GET_DISCOUNT_PERCENT];
      products.forEach((product, productId) => {
        const variant = product.variant;
        const price = variant.new_price ? Number(variant.new_price) : Number(product.price);
        const quantity = product.quantity ? Number(product.quantity) : 0;
        const discountTotal = percentOf(quantity * price, discountPercent);
        const total = (new Decimal(price)).mul(quantity).plus(discountTotal).toFixed(2, Decimal.ROUND_HALF_EVEN);
        commit(types.ORDER_SET_PRODUCTS_TOTAL, {productId, total});
      });
    }, /**
     * Updates the variant of a product in the order.
     * @param {Object} context - The Vuex context.
     * @param {Object} payload - The payload containing productId and variantId.
     */
    [types.ORDER_PRODUCTS_UPDATE_VARIANT]({state, commit, rootGetters}, {productId, variantId}) {
      const product = state.items[productId];
      const variant = product.variants.find(variant => {
        return variant.id === variantId;
      });
      const quantity = product.quantity ? Number(product.quantity) : 0;
      const price = Number(variant.price);
      const discountPercent = rootGetters[types.ORDER_GET_DISCOUNT_PERCENT];
      const discountTotal = percentOf(quantity * price, discountPercent);
      const total = (quantity * price) + discountTotal;
      variant['new_price'] = price;
      commit(types.ORDER_SET_PRODUCTS_VARIANT, {productId, variant});
      commit(types.ORDER_SET_PRODUCTS_TOTAL, {productId, total});
    },
  }, getters: {
    /**
     * Gets the line items for the products in the order.
     * @param {Object} state - The current state.
     * @param {Object} getters - The Vuex getters.
     * @param {Object} rootState - The root state.
     * @returns {Array} The line items for the products.
     */
    [types.ORDER_GET_PRODUCTS_LINE_ITEMS](state, getters, rootState) {
      const products = _.cloneDeep(state.items);
      const reducer = (lineItems, product) => {
        const appliedDiscount = appliedDiscountFactory(product);
        const {variant, line_item: lineItem} = product;
        const taxable = lineItem ? sumTaxLines(lineItem['tax_lines']) > 0 : Number(rootState.order.order.total_tax) > 0;

        if (appliedDiscount && Number(appliedDiscount.amount) < 0) {
          lineItems.push({
            sku: variant.sku,
            product_id: variant.product_id,
            variant_id: variant.id,
            title: product.title,
            quantity: product.quantity,
            price: variant.new_price.toFixed(2),
            taxable: taxable,
            custom: true,
          });
        } else {
          lineItems.push({
            sku: variant.sku,
            variant_id: variant.id,
            product_id: variant.product_id,
            title: product.title,
            quantity: product.quantity,
            price: variant.new_price,
            applied_discount: appliedDiscount,
            taxable: taxable,
          });
        }

        return lineItems;
      };
      return products.reduce(reducer, []);
    }, /**
     * Gets the progress of the products in the order.
     * @param {Object} state - The current state.
     * @param {Object} getters - The Vuex getters.
     * @param {Object} rootState - The root state.
     * @returns {number} The progress of the products in the order.
     */
    [types.ORDER_GET_PRODUCTS_PROGRESS](state, getters, rootState) {
      const orderLineItemsCount = rootState.order.order.line_items.length;
      const productsLineItemsCount = state.items.length;
      const progress = productsLineItemsCount / orderLineItemsCount * 100;
      return progress > 100 ? 100 : progress;
    }, /**
     * Gets the total quantity of the products in the order.
     * @param {Object} state - The current state.
     * @returns {number} The total quantity of the products.
     */
    [types.ORDER_GET_PRODUCTS_QUANTITY_COUNT](state) {
      const products = _.cloneDeep(state.items);
      const reducer = (totalQuantity, product) => {
        const quantity = Number(product.quantity) ? Number(product.quantity) : 0;
        return Number(totalQuantity) + quantity;
      };
      return products.reduce(reducer, 0);
    }, /**
     * Gets the subtotal of the products in the order.
     * @param {Object} state - The current state.
     * @returns {number} The subtotal of the products.
     */
    [types.ORDER_GET_PRODUCTS_SUBTOTAL](state) {
      const products = _.cloneDeep(state.items);
      const reducer = (subtotal, product) => {
        const {quantity} = product;
        const {variant} = product;
        const {new_price: newPrice} = variant;

        return (new Decimal(newPrice)).times(quantity).plus(subtotal);
      };
      return products.reduce(reducer, new Decimal(0)).toNumber();
    },
  },
};